/**
 * Boost speed-lines effect — anime/comic style streaks at the viewport edges
 * that zip toward the center while the player is boosting.
 *
 * Implementation:
 *   - Pool of line particles in *screen* space (not world). Each has a
 *     position, a velocity heading toward the viewport center, life, and
 *     length.
 *   - While `intensity > 0`, new lines spawn from random viewport-edge
 *     positions at a rate proportional to intensity.
 *   - Existing lines move inward, fading by life.
 *
 * Rendered above gameplay (so the streaks read as foreground motion) but
 * under the HUD. Tinted in the player's team hue.
 */

interface SpeedLine {
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

export class SpeedLines {
  private readonly pool: SpeedLine[] = [];
  private spawnAccum = 0;

  constructor(capacity = 80) {
    for (let i = 0; i < capacity; i++) {
      this.pool.push({ alive: false, x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1 });
    }
  }

  /**
   * Advance the system.
   * @param intensity 0..1 — 1 = full boost speed-lines spawning.
   */
  step(dt: number, intensity: number, vw: number, vh: number): void {
    // Move + age existing lines.
    const pool = this.pool;
    for (let i = 0; i < pool.length; i++) {
      const l = pool[i]!;
      if (!l.alive) continue;
      l.x += l.vx * dt;
      l.y += l.vy * dt;
      l.life -= dt;
      if (l.life <= 0) l.alive = false;
    }

    // Spawn new lines. Spawn rate scales with intensity^2 so subtle boosts
    // give few lines and full boost gives a moderate flood — calibrated to
    // suggest speed without overwhelming the player's view.
    if (intensity > 0.05) {
      const rate = 28 * intensity * intensity;
      this.spawnAccum += dt * rate;
      while (this.spawnAccum >= 1) {
        this.spawnAccum -= 1;
        this.spawn(vw, vh);
      }
    } else {
      this.spawnAccum = 0;
    }
  }

  private spawn(vw: number, vh: number): void {
    const slot = this.findFree();
    if (!slot) return;
    // Pick an edge point biased away from the center so lines come from
    // around the screen.
    const cx = vw * 0.5;
    const cy = vh * 0.5;
    // Random angle around center, distance just past the corner-radius.
    const ang = Math.random() * Math.PI * 2;
    const dist = Math.max(vw, vh) * 0.65;
    const px = cx + Math.cos(ang) * dist;
    const py = cy + Math.sin(ang) * dist;
    // Velocity: head INWARD toward center, with a fast speed so lines whip past.
    const dx = cx - px;
    const dy = cy - py;
    const len = Math.hypot(dx, dy) || 1;
    const speed = 900 + Math.random() * 400;
    slot.x = px;
    slot.y = py;
    slot.vx = (dx / len) * speed;
    slot.vy = (dy / len) * speed;
    slot.maxLife = 0.14 + Math.random() * 0.08;
    slot.life = slot.maxLife;
    slot.alive = true;
  }

  private findFree(): SpeedLine | null {
    const pool = this.pool;
    for (let i = 0; i < pool.length; i++) {
      if (!pool[i]!.alive) return pool[i]!;
    }
    return null;
  }

  draw(ctx: CanvasRenderingContext2D, hue: number): void {
    const pool = this.pool;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineWidth = 1.4;
    ctx.shadowColor = `hsl(${hue} 100% 75%)`;
    ctx.shadowBlur = 5;
    for (let i = 0; i < pool.length; i++) {
      const l = pool[i]!;
      if (!l.alive) continue;
      // Shorter tail than before so streaks read as "fast", not "long ribbons".
      const tailX = l.x - l.vx * 0.025;
      const tailY = l.y - l.vy * 0.025;
      const alpha = Math.max(0, l.life / l.maxLife) * 0.5;
      ctx.strokeStyle = `hsla(${hue} 100% 82% / ${alpha})`;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(l.x, l.y);
      ctx.stroke();
    }
    ctx.restore();
  }
}
